import { BackgroundModel } from '../engine/models/background.model';
import { CameraModel } from '../engine/models/camera.model';
import { ColorUtils } from '../shared/libraries/colors/colors';
import { XYZModel } from '../shared/models/xyz.model';
import { MediaEnum } from '../story/models/media.enum';
import { ViewLayoutConfig } from '../view/models/view-layout-config.enum';
import { ViewLoaderEnum } from '../view/models/view-loader.enum';
import { ViewQuizModeEnum } from '../view/models/view-quiz-mode.enum';

export interface RawParams {
  [prop: string]: string;
}

interface ParamTransform {
  [prop: string]: (params: RawParams) => any;
}

type ParamValueTypes = string | number | boolean | BackgroundModel | CameraModel;
type ValueTransformer = (input: string) => ParamValueTypes;

interface ParamValueTransform {
  [prop: string]: ValueTransformer;
}

export interface UrlUIConfig {
  'annotation-collapse'?: number; // alias to ui-collapse-label ,
  'label-scale'?: number;
  'ui-all'?: boolean;
  'ui-anatomy-descriptions'?: boolean;
  'ui-anatomy-labels'?: boolean;
  'ui-audio'?: boolean;
  'ui-bd.link'?: boolean;
  'ui-bd'?: boolean;
  'ui-chapter-list'?: boolean;
  'ui-collapse-label'?: number;
  'ui-context-actions'?: boolean;
  'ui-fullscreen'?: boolean;
  'ui-help'?: boolean;
  'ui-info-expand'?: boolean;
  'ui-info'?: boolean;
  'ui-labels'?: boolean;
  'ui-label-list'?: boolean;
  'ui-layers'?: boolean; // alias to ui-visibility-layers
  'ui-layout'?: ViewLayoutConfig;
  'ui-legend'?: boolean;
  'ui-loader'?: ViewLoaderEnum;
  'ui-logo.islink'?: boolean;
  'ui-logo'?: boolean;
  'ui-media-controls'?: MediaEnum;
  'ui-menu'?: boolean;
  'ui-nav'?: boolean;
  'ui-object-tree'?: boolean; // alias to ui-search
  'ui-panel'?: boolean; // alias to ui-info
  'ui-presenter'?: boolean;
  'ui-print-image'?: boolean;
  'ui-scrubber.chapters'?: boolean;
  'ui-scrubber.tour'?: boolean;
  'ui-scrubber'?: boolean;
  'ui-search'?: boolean;
  'ui-tools'?: boolean;
  'ui-tour'?: boolean;
  'ui-tutorial'?: boolean;
  'ui-undo'?: boolean;
  'ui-visibility-layers'?: boolean;
  'ui-whiteboard'?: boolean;
  // quiz enabled
  'ui-quiz'?: boolean;
  // quiz locked
  'ui-quiz-locked'?: boolean;
  // quiz question range
  'ui-quiz-start'?: number;
  'ui-quiz-end'?: number;
}

interface BaseConfig {
  id?: string;
  type?: string;

  'uaid'?: string;
  'dk'?: string;
  'app-productkey'?: string;
  'app-userid'?: string;

  // background color
  'background'?: BackgroundModel;

  // camera
  'camera'?: CameraModel;

  // load rotate
  'load-rotate'?: number;

  'auto-load'?: boolean;
}

export type WidgetUrlConfig = BaseConfig & UrlUIConfig;


const depreactedUIConfigNames = [
  'ui-bd.class',
  'ui-play-pause',
  'ui-camera-mode',
  'ui-center',
  'ui-cross-section',
  'ui-dissect',
  'ui-fs',
  'ui-tools-display',
  'ui-tree',
  'ui-zoom',
  'ui-zoom-in',
  'ui-zoom-out',

  'ui-pan',
  'ui-share',
  'ui-keys',
  'ui-slider'
];

const depreactedNonUIConfigNames = [
  'o',
  'background.color-stops',
  'background.gradient-type',
  'dd',
  'dn',
  'image-display',
  'imageDisplay',
  'imageSrc',
  'pre',
  'referrer'
];

export const deprecatedConfigNames = [...depreactedUIConfigNames, ...depreactedNonUIConfigNames];

// normalizes legacy id params (s, m, be, b) into standard id
export function normalizeQueryIdParam(config: RawParams): string {
  const { id, s, m, be, b } = config;

  let contentId = '';

  if (id) { // pass-through id
    contentId = id;
  } else if (be) { // encoded bookmark id
    contentId = be;
  } else if (b) { // decoded bookmark id
    const bookmarkId = parseInt(b, 10);
    contentId = Number.isNaN(bookmarkId) ? '' : encodeBookmarkId(bookmarkId);
  } else if (m) { // module id
    const sex = s === 'female' ? 'female' : 'male';
    contentId = normalizeModuleId(m, sex);
  }

  // trim trailing .json 
  return contentId.replace(/\.json$/, '');
}

export function normalizeQueryTypeParam(config: RawParams): 'bookmark' | 'module' {
  // determine type by id
  const contentId = normalizeQueryIdParam(config);
  return contentId.includes('/') ? 'module' : 'bookmark';
}

// ported from: https://github.com/biodigital-human/human-cms/blob/f8608ac6c523318b4a21ac76b200dbd3928abc1f/content/bookmarks.py#L46
function encodeBookmarkId(bookmarkId: number) {
  const BASE62_IDX = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';

  const divmod = (x: number, y: number): [number, number] => {
    const quotient = Math.floor(x / y);
    const remainder = x % y;
    return [quotient, remainder];
  };

  const encoded: string[] = [];
  let num: number = bookmarkId;
  let digit: number;

  while (num > 0) {
    [num, digit] = divmod(num, 62);
    encoded.unshift(BASE62_IDX[digit]);
  }
  return encoded.join('');
}

function normalizeModuleId(id: string, sex: 'male' | 'female' = 'male') {
  return id.includes('/')
    ? id // use full module name
    : `production/${sex}Adult/${id}`; // normalize short module names    
}

// normalize into single params
const paramTransforms: ParamTransform = {
  'type': (params) => normalizeQueryTypeParam(params),
  'id': (p) => normalizeQueryIdParam(p),
  // background normalization
  'background': (p) => p['bgstd'] || p['background.colors'] || p['background']
};

function dropDeprecatedParams(config: RawParams) {
  const configKeys = Object.keys(config);
  const deprecatedKeys = configKeys
    .filter((key) => deprecatedConfigNames.includes(key));

  // warning: deprecated params
  if (deprecatedKeys.length > 0) {
    const messages = [
      `The following URL parameters have been deprecated: "${deprecatedKeys.join('", "')}"`,
      `Please contact support@biodigital.com for more information.`
    ];
    console.warn(messages.join('\n'));
  }

  return configKeys.reduce((acc, key) => {

    // drop if deprecated
    if (deprecatedConfigNames.includes(key)) {
      delete acc[key];
    }

    return acc;
  }, config);
}


function transformParams(config: RawParams) {
  return Object.keys(paramTransforms)
    // exclude already defined params
    .filter(targetParamId => typeof config[targetParamId] === 'undefined')
    // transform many -> one params
    .reduce((acc, targetParamId) => {
      const transform = paramTransforms[targetParamId];
      const transformedValue = transform(config);
      if (typeof (transformedValue) !== 'undefined') {
        acc[targetParamId] = transformedValue;
      }
      return acc;
    }, config);
}

function toXYZ(values: number[]): XYZModel {
  if (values.length === 3) {
    const { 0: x, 1: y, 2: z } = values;
    return { x, y, z };
  } else {
    return null;
  }
}

function parseCamera(input: string): CameraModel {
  // comma seperated camera values -> number[]
  const values = input
    .split(',')
    .map((entry) => parseFloat(entry));

  // validate required 3 part camera values (eye required) + valid numbers
  const valid = values.length % 3 === 0 && !values.some((value) => Number.isNaN(value));

  if (valid) {
    // required: eye
    const eyeValues = values.slice(0, 3);
    // optional additions
    const lookValues = values.slice(3, 6);
    const upValues = values.slice(6, 9);

    return {
      eye: toXYZ(eyeValues),
      look: toXYZ(lookValues),
      up: toXYZ(upValues)
    };
  } else {
    return null;
  }
}

function parseBackground(input: string): BackgroundModel {
  // parse color
  const colorValues = ColorUtils.getColors(input);

  if (colorValues) {
    // get colors (first + second) as 255
    const color1 = colorValues[0]['255'];
    const color2 = colorValues[1]['255'];

    // create background model
    const colors = [...color1, ...color2];
    const gradientType = 'radial';
    const colorStops: [number, number] = [0, 100];
    return new BackgroundModel(colors, gradientType, colorStops);
  } else {
    return null;
  }
}

const paramValueTransform: ParamValueTransform = {
  'camera': parseCamera,
  'background': parseBackground
};

// coerce strings to booleans where appropriate
function coerceValues(config: RawParams): WidgetUrlConfig {
  const coerceValue: ValueTransformer = (value: string) => {    
    if (value) {
      const normalizedValue = value.toLowerCase();
      if (normalizedValue === 'true') {
        return true;
      } else if (normalizedValue === 'false') {
        return false;
      } else if (!isNaN(Number(normalizedValue))) {
        return Number(normalizedValue);
      }
    }
    return value;
  };

  return Object.keys(config).reduce((acc, key) => {
    // get custom value transformer else default
    const valueTransformer: ValueTransformer = paramValueTransform[key] || coerceValue;
    const value = valueTransformer(config[key]);


    // patch for JnJ
    // sanitize load rotate
    // Later: define coercion for each property instead of making assumptions.
    if (key === 'load-rotate' && isNaN(Number(value))) {
      return acc;
    }

    acc[key] = value;
    return acc;
  }, {} as WidgetUrlConfig);
}

export function getInitialWidgetConfig(rawParams: RawParams): WidgetUrlConfig {
  const withoutDeprecated = dropDeprecatedParams(rawParams);
  const transformedConfig = transformParams(withoutDeprecated);
  const coercedConfig = coerceValues(transformedConfig);
  return coercedConfig;
}
